const getMessageTotalCost = (msg) => {
    let cost = parseFloat(msg.price) || 0;
    if (msg.regenerations && Array.isArray(msg.regenerations)) {
        cost += msg.regenerations.reduce((sum, r) => sum + (parseFloat(r.price) || 0), 0);
    }
    return cost;
};

const calculateHistoryTotal = (history, activeSessionId) => {
    return (history || [])
        .filter(h => h.id !== activeSessionId)
        .reduce((sum, h) => {
            const sessionSum = (h.messages || [])
                .filter(m => m.sender === 'bot')
                .reduce((s, m) => s + getMessageTotalCost(m), 0);
            return sum + sessionSum;
        }, 0);
};

const initialState = {
    messages: [], // Array of { sender: 'user'|'bot', text: string, databaseTimeMs?: number, apiTimeMs?: number, isStreaming?: boolean }
    history: [],  // Array of { id: string, title: string, messages: array, timestamp: number }
    loading: false,
    databaseTimeMs: 0,
    apiTimeMs: 0,
    error: null,
    errorStatus: null, // 'api_error' | 'server_error' | 'network_error'
    activeSessionId: null,
    price: 0,
    totalPrice: 0,
    historyTotalPrice: 0,
    topicRecommendation: null,
    skillRecommendation: null
};

const ChatbotReducers = (state = initialState, action) => {
    switch (action.type) {
        case "CHATBOT_LOAD_HISTORY": {
            const history = action.history || [];
            const historyTotalPrice = calculateHistoryTotal(history, state.activeSessionId);
            const currentTotal = (state.messages || [])
                .filter(m => m.sender === 'bot')
                .reduce((sum, m) => sum + getMessageTotalCost(m), 0);
            return {
                ...state,
                history,
                historyTotalPrice,
                totalPrice: historyTotalPrice + currentTotal
            };
        }

        case "CHATBOT_SET_LOADING":
            return {
                ...state,
                loading: action.loading
            };

        case "CHATBOT_SET_ERROR": {
            let updatedMessages = [...state.messages];
            const isSubChat = action.isSubChat !== undefined ? action.isSubChat : (updatedMessages.length > 2);

            if (updatedMessages.length > 0) {
                const last = updatedMessages[updatedMessages.length - 1];
                if (last.sender === 'bot' && last.text === '') {
                    updatedMessages.pop();
                }
            }

            if (!isSubChat) {
                updatedMessages = [];
            }

            return {
                ...state,
                messages: updatedMessages,
                error: action.error,
                errorStatus: action.errorStatus || null
            };
        }

        case "CHATBOT_ADD_MESSAGE":
            return {
                ...state,
                messages: [...state.messages, action.message]
            };

        case "CHATBOT_UPDATE_STREAMING_CHUNK": {
            const updatedMessages = [...state.messages];
            if (updatedMessages.length > 0) {
                const last = updatedMessages[updatedMessages.length - 1];
                if (last.sender === 'bot') {
                    updatedMessages[updatedMessages.length - 1] = {
                        ...last,
                        text: last.text + action.text,
                        databaseTimeMs: (action.databaseTimeMs !== undefined && action.databaseTimeMs !== null && action.databaseTimeMs !== 0) ? action.databaseTimeMs : last.databaseTimeMs,
                        apiTimeMs: (action.apiTimeMs !== undefined && action.apiTimeMs !== null && action.apiTimeMs !== 0) ? action.apiTimeMs : last.apiTimeMs,
                        sendTokens: (action.sendTokens !== undefined && action.sendTokens !== null && action.sendTokens !== 0) ? action.sendTokens : last.sendTokens,
                        receivedTokens: (action.receivedTokens !== undefined && action.receivedTokens !== null && action.receivedTokens !== 0) ? action.receivedTokens : last.receivedTokens,
                        sendTimeMs: (action.sendTimeMs !== undefined && action.sendTimeMs !== null && action.sendTimeMs !== 0) ? action.sendTimeMs : last.sendTimeMs,
                        responseTimeMs: (action.responseTimeMs !== undefined && action.responseTimeMs !== null && action.responseTimeMs !== 0) ? action.responseTimeMs : last.responseTimeMs,
                        serverTimeMs: (action.serverTimeMs !== undefined && action.serverTimeMs !== null && action.serverTimeMs !== 0) ? action.serverTimeMs : last.serverTimeMs,
                        isFromCache: (action.isFromCache !== undefined && action.isFromCache !== null) ? action.isFromCache : last.isFromCache,
                        cacheRemainingTime: (action.cacheRemainingTime !== undefined && action.cacheRemainingTime !== null) ? action.cacheRemainingTime : last.cacheRemainingTime,
                        searchWords: (action.searchWords !== undefined && action.searchWords !== null) ? action.searchWords : last.searchWords,
                        bandwidth: (action.bandwidth !== undefined && action.bandwidth !== null && action.bandwidth !== 0) ? action.bandwidth : last.bandwidth,
                        sendTokensDetails: (action.sendTokensDetails !== undefined && action.sendTokensDetails !== null) ? action.sendTokensDetails : last.sendTokensDetails,
                        statusText: (action.statusText !== undefined && action.statusText !== null) ? action.statusText : last.statusText,
                        model: (action.model !== undefined && action.model !== null) ? action.model : last.model,
                        price: (action.price !== undefined && action.price !== null) ? action.price : last.price,
                        prompt: (action.prompt !== undefined && action.prompt !== null) ? action.prompt : last.prompt,
                        questions: (action.questions !== undefined && action.questions !== null) ? action.questions : last.questions,
                        preloadedQuestions: (action.preloadedQuestions !== undefined && action.preloadedQuestions !== null) ? action.preloadedQuestions : last.preloadedQuestions,
                        relativeLessons: (action.relativeLessons !== undefined && action.relativeLessons !== null) ? action.relativeLessons : last.relativeLessons,
                        mainSubject: (action.mainSubject !== undefined && action.mainSubject !== null) ? action.mainSubject : last.mainSubject,
                        category: (action.category !== undefined && action.category !== null) ? action.category : last.category
                    };
                }
            }
            const newPrice = (action.price !== undefined && action.price !== null) ? action.price : state.price;
            const currentTotal = updatedMessages
                .filter(m => m.sender === 'bot')
                .reduce((sum, m) => sum + getMessageTotalCost(m), 0);

            return {
                ...state,
                messages: updatedMessages,
                databaseTimeMs: (action.databaseTimeMs !== undefined && action.databaseTimeMs !== null && action.databaseTimeMs !== 0) ? action.databaseTimeMs : state.databaseTimeMs,
                apiTimeMs: (action.apiTimeMs !== undefined && action.apiTimeMs !== null && action.apiTimeMs !== 0) ? action.apiTimeMs : state.apiTimeMs,
                price: newPrice,
                totalPrice: (state.historyTotalPrice || 0) + currentTotal
            };
        }

        case "CHATBOT_FINISH_STREAMING": {
            const updatedMessages = [...state.messages];
            if (updatedMessages.length > 0) {
                const last = updatedMessages[updatedMessages.length - 1];
                if (last.sender === 'bot') {
                    let newQuestions = last.questions || [];
                    const preloaded = last.preloadedQuestions || [];
                    if (preloaded.length > 0) {
                        const shuffled = [...preloaded].sort(() => 0.5 - Math.random());
                        newQuestions = shuffled.slice(0, Math.min(10, shuffled.length));
                    }

                    let updatedText = last.text || '';
                    if (newQuestions.length > 0) {
                        const cleanText = updatedText.replace(/<script\s+id=['"]lesson-questions-data['"][\s\S]*$/i, '').trim();
                        const newScriptTag = `<script id='lesson-questions-data' type='application/json'>${JSON.stringify(newQuestions)}</script>`;
                        updatedText = `${cleanText}\n\n${newScriptTag}`;
                    }

                    let hist = last.questionsHistory || [];
                    if (newQuestions.length > 0 && hist.length === 0) {
                        hist = [{
                            timestamp: Date.now(),
                            questions: newQuestions
                        }];
                    }
                    updatedMessages[updatedMessages.length - 1] = {
                        ...last,
                        text: updatedText,
                        questions: newQuestions,
                        isStreaming: false,
                        questionsHistory: hist
                    };
                }
            }
            return {
                ...state,
                messages: updatedMessages,
                loading: false
            };
        }

        case "CHATBOT_LOAD_SESSION": {
            const sessionMessages = action.messages || [];
            const historyTotalPrice = calculateHistoryTotal(state.history, action.sessionId);
            const currentTotal = sessionMessages
                .filter(m => m.sender === 'bot')
                .reduce((sum, m) => sum + getMessageTotalCost(m), 0);
            const existingSession = state.history.find(h => h.id === action.sessionId);
            const topicRecommendation = existingSession ? (existingSession.nextSkillRecommendation || existingSession.nextTopicRecommendation) : null;
            return {
                ...state,
                activeSessionId: action.sessionId,
                messages: sessionMessages,
                databaseTimeMs: action.databaseTimeMs || 0,
                apiTimeMs: action.apiTimeMs || 0,
                sendTokens: action.sendTokens || 0,
                receivedTokens: action.receivedTokens || 0,
                sendTimeMs: action.sendTimeMs || 0,
                responseTimeMs: action.responseTimeMs || 0,
                serverTimeMs: action.serverTimeMs || 0,
                isFromCache: action.isFromCache || false,
                cacheRemainingTime: action.cacheRemainingTime || null,
                bandwidth: action.bandwidth || 0,
                sendTokensDetails: action.sendTokensDetails || null,
                model: action.model || null,
                price: action.price || 0,
                prompt: action.prompt || null,
                error: null,
                errorStatus: null,
                loading: false,
                historyTotalPrice,
                totalPrice: historyTotalPrice + currentTotal,
                topicRecommendation,
                skillRecommendation: topicRecommendation
            };
        }

        case "CHATBOT_CLEAR_CHAT": {
            const historyTotalPrice = calculateHistoryTotal(state.history, state.activeSessionId);
            return {
                ...state,
                messages: [],
                databaseTimeMs: 0,
                apiTimeMs: 0,
                price: 0,
                historyTotalPrice,
                totalPrice: historyTotalPrice,
                error: null,
                errorStatus: null,
                topicRecommendation: null
            };
        }

        case "CHATBOT_START_NEW_CHAT": {
            const historyTotalPrice = calculateHistoryTotal(state.history, action.sessionId);
            return {
                ...state,
                activeSessionId: action.sessionId,
                messages: [],
                databaseTimeMs: 0,
                apiTimeMs: 0,
                price: 0,
                historyTotalPrice,
                totalPrice: historyTotalPrice,
                error: null,
                errorStatus: null,
                loading: false,
                topicRecommendation: null
            };
        }

        case "CHATBOT_UPDATE_SESSION_ID": {
            const oldId = state.activeSessionId;
            const newId = action.sessionId;
            const updatedHistory = state.history.map(h => {
                if (h.id === oldId) {
                    return { ...h, id: newId };
                }
                return h;
            });
            return {
                ...state,
                activeSessionId: newId,
                history: updatedHistory
            };
        }

        case "CHATBOT_DELETE_HISTORY":
            return {
                ...state,
                history: [],
                messages: [],
                activeSessionId: null,
                databaseTimeMs: 0,
                apiTimeMs: 0,
                price: 0,
                historyTotalPrice: 0,
                totalPrice: 0,
                error: null,
                errorStatus: null,
                loading: false,
                topicRecommendation: null
            };

        case "CHATBOT_UPDATE_MESSAGES": {
            const currentTotal = action.messages
                .filter(m => m.sender === 'bot')
                .reduce((sum, m) => sum + getMessageTotalCost(m), 0);
            return {
                ...state,
                messages: action.messages,
                totalPrice: state.historyTotalPrice + currentTotal
            };
        }

        case "CHATBOT_SET_SKILL_RECOMMENDATION":
        case "CHATBOT_SET_TOPIC_RECOMMENDATION":
            return {
                ...state,
                topicRecommendation: action.recommendation,
                skillRecommendation: action.recommendation
            };

        default:
            return state;
    }
};

export default ChatbotReducers;
